import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Paper, Popper, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from 'react-i18next';
import { localStorageItems } from 'setup/localStorage';
import UAFlag from 'assets/UA.svg?react';
import ENFlag from 'assets/GB.svg?react';
import { supportedLangs } from 'setup/i18n';

interface IAppLanguageSelectProps {}

interface LangMeta {
  code: supportedLangs;
  // Short display code shown in the trigger button (matches the prototype's
  // UA/EN convention — two-letter, uppercase, easier to scan than UKR/ENG).
  short: string;
  label: string;
  Flag: React.FC<React.SVGProps<SVGSVGElement>>;
}

const LANGUAGES: LangMeta[] = [
  { code: 'uk', short: 'UA', label: 'Українська', Flag: UAFlag },
  { code: 'en', short: 'EN', label: 'English', Flag: ENFlag },
];

// 1280px mirrors AppHeader.HAMBURGER_BREAKPOINT. Above that the picker
// opens on hover (with a small close delay so the pointer can travel
// from button to panel); below, it falls back to tap-to-toggle because
// hover-on-touch is unreliable.
const DESKTOP_QUERY = '(min-width: 1280px)';
// 600px (MUI default sm) below which we drop the code + caret and render
// a flag-only chip. Saves horizontal room on phones.
const COMPACT_QUERY = '(max-width: 599.95px)';

const AppLanguageSelect: React.FC<IAppLanguageSelectProps> = () => {
  const { i18n } = useTranslation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const compact = useMediaQuery(COMPACT_QUERY);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimeoutRef = useRef<number | undefined>(undefined);
  const open = Boolean(anchorEl);

  const currentLanguage = i18n.language as supportedLangs;
  const currentMeta = useMemo(
    () => LANGUAGES.find((l) => l.code === currentLanguage) ?? LANGUAGES[0],
    [currentLanguage],
  );

  const clearCloseTimeout = () => {
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = undefined;
    }
  };

  const scheduleClose = () => {
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(
      () => setAnchorEl(null),
      180,
    );
  };

  const closeNow = () => {
    clearCloseTimeout();
    setAnchorEl(null);
  };

  // Cleanup on unmount so a pending close doesn't fire against a stale
  // setState after navigation.
  useEffect(() => () => clearCloseTimeout(), []);

  const handleTriggerHover = (e: React.MouseEvent<HTMLElement>) => {
    if (!isDesktop) return;
    clearCloseTimeout();
    setAnchorEl(e.currentTarget);
  };

  const handleTriggerClick = (e: React.MouseEvent<HTMLElement>) => {
    if (isDesktop) return;
    setAnchorEl((curr) => (curr ? null : e.currentTarget));
  };

  const changeLanguage = (lang: supportedLangs) => {
    closeNow();
    if (lang === currentLanguage) return;
    i18n.changeLanguage(lang);
    localStorage.setItem(localStorageItems.appLanguage, lang);
    window.location.reload();
  };

  const renderFlag = (
    Flag: LangMeta['Flag'],
    size: number,
    style?: React.CSSProperties,
  ) => (
    <Flag
      width={size}
      height={size}
      style={{ borderRadius: 2, flex: 'none', ...style }}
    />
  );

  const primaryHoverBg =
    theme.palette.mode === 'light'
      ? 'rgba(59, 44, 147, 0.06)'
      : 'rgba(155, 124, 255, 0.10)';

  // ── Trigger ────────────────────────────────────────────────────────────
  const trigger = compact ? (
    // Compact phone chip — flag only, no border, no caret. Tap toggles.
    <Box
      component={'button'}
      type={'button'}
      aria-haspopup={'menu'}
      aria-expanded={open}
      aria-label={'Мова інтерфейсу'}
      onClick={handleTriggerClick}
      sx={{
        alignItems: 'center',
        // Match the header surface (UICard uses background.paper) so the
        // chip blends into the bar instead of standing out as a tinted
        // pill. Hover/open state is signalled by the border, not the fill.
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: open ? 'primary.main' : 'transparent',
        borderRadius: 1.25,
        cursor: 'pointer',
        display: 'inline-flex',
        height: 40,
        justifyContent: 'center',
        p: 0,
        transition: 'background-color .14s ease, border-color .14s ease',
        width: 40,
      }}
    >
      {renderFlag(currentMeta.Flag, 20)}
    </Box>
  ) : (
    <Box
      component={'button'}
      type={'button'}
      aria-haspopup={'menu'}
      aria-expanded={open}
      onMouseEnter={handleTriggerHover}
      onMouseLeave={isDesktop ? scheduleClose : undefined}
      onClick={handleTriggerClick}
      sx={{
        alignItems: 'center',
        // Fill always matches the header surface (background.paper) — that
        // way the chip reads as part of the bar regardless of theme mode.
        // The border carries the open/closed signal.
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: open ? 'primary.main' : 'primary.tableBorder',
        borderRadius: 1.25,
        color: 'text.primary',
        cursor: 'pointer',
        display: 'inline-flex',
        fontFamily: 'inherit',
        fontSize: 14,
        fontWeight: 600,
        gap: 0.75,
        height: 38,
        px: 1.25,
        transition: 'background-color .14s ease, border-color .14s ease',
        '&:hover': {
          borderColor: 'primary.main',
        },
      }}
    >
      {renderFlag(currentMeta.Flag, 18)}
      <Box component={'span'} sx={{ letterSpacing: '0.02em' }}>
        {currentMeta.short}
      </Box>
      <KeyboardArrowDownIcon
        sx={{
          color: 'text.secondary',
          fontSize: 18,
          transform: open ? 'rotate(180deg)' : 'none',
          transition: 'transform .18s ease',
        }}
      />
    </Box>
  );

  return (
    <Box sx={{ display: 'inline-flex' }}>
      {trigger}
      <Popper
        open={open}
        anchorEl={anchorEl}
        placement={'bottom-end'}
        modifiers={[
          { name: 'offset', options: { offset: [0, 8] } },
          { name: 'preventOverflow', options: { padding: 12 } },
        ]}
        sx={{ zIndex: (t) => t.zIndex.modal }}
      >
        <Paper
          elevation={0}
          // Click-outside is handled by tapping the trigger again on
          // mobile or by hovering off + the close timeout on desktop.
          // Mouse enter on the panel keeps it open across the gap.
          onMouseEnter={isDesktop ? clearCloseTimeout : undefined}
          onMouseLeave={isDesktop ? scheduleClose : undefined}
          sx={{
            border: '1px solid',
            borderColor: 'primary.tableBorder',
            borderRadius: 2,
            boxShadow: '0 24px 60px -20px rgba(20,20,20,0.45)',
            p: 1,
            width: 200,
          }}
        >
          {LANGUAGES.map((l) => {
            const on = l.code === currentLanguage;
            return (
              <Box
                key={l.code}
                onClick={() => changeLanguage(l.code)}
                sx={{
                  alignItems: 'center',
                  bgcolor: on ? primaryHoverBg : 'transparent',
                  borderRadius: 1,
                  cursor: 'pointer',
                  display: 'flex',
                  gap: 1.25,
                  px: 1.25,
                  py: 1,
                  transition: 'background-color .12s ease',
                  '&:hover': { bgcolor: primaryHoverBg },
                }}
              >
                {renderFlag(l.Flag, 20)}
                <Typography
                  sx={{
                    color: on ? 'primary.main' : 'text.primary',
                    flex: 1,
                    fontSize: 14.5,
                    fontWeight: on ? 700 : 500,
                  }}
                >
                  {l.label}
                </Typography>
                {on && (
                  <CheckIcon
                    sx={{ color: 'primary.main', fontSize: 18 }}
                  />
                )}
              </Box>
            );
          })}
        </Paper>
      </Popper>
    </Box>
  );
};

export default AppLanguageSelect;
